import axios from 'axios';
// eslint-disable-next-line @typescript-eslint/naming-convention
import jwt_decode from 'jwt-decode';
import { BehaviorSubject, distinctUntilChanged, filter, map, shareReplay, skip } from 'rxjs';
import { UserId } from 'api/models';
import { getCurrentApiHost, API_NAMESPACE, API_VERSION } from 'config/environment';
import { InitWorkerApi } from 'initWorker';
// import { MARKETPLACE_AUTH_TOKEN_QUERY_KEY } from 'marketplace/hooks/use-marketplace-hooks';
import { isLinkMagicToken } from 'utils/token';
import { ActiveTab } from './active-tab';
import { logger } from './logger';
let okta = null;
export const setOkta = (auth)=>{
    okta = auth;
};
export const getOkta = ()=>{
    return okta;
};
const getDecodedValue = (token)=>{
    try {
        return jwt_decode(token);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
        return null;
    }
};
export const TOKEN_EXPIRY_KEY = 'token.authExpiry';
const key = '__api_key';
export const API_KEY = key;
const sanitize = (value)=>JSON.parse(value !== null && value !== void 0 ? value : 'null');
const get = ()=>sanitize(localStorage.getItem(key));
const _apiKey$ = new BehaviorSubject(get());
_apiKey$.pipe(skip(1), filter((k)=>k == null)).subscribe(()=>{
    ActiveTab.save();
    /**
     * A redirect or window.location.reload() is recommended to ensure proper
     * re-initialization of app and okta auth sdk. Refer:
     * https://www.npmjs.com/package/@okta/okta-auth-js#closesession
     */ window.location.reload();
});
window.addEventListener('storage', (event)=>{
    if (event.storageArea !== localStorage || event.key !== key) {
        return;
    }
    _apiKey$.next(sanitize(event.newValue));
});
const exists = ()=>get() != null;
const creator = ([, , author])=>UserId(author);
const clear = async ()=>{
    const api_key = get();
    if (api_key == null) {
        return;
    }
    localStorage.removeItem(key);
    try {
        await axios.delete('users/delete-api-key', {
            baseURL: `${getCurrentApiHost()}/${API_NAMESPACE}/${API_VERSION}`,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            headers: {
                'api-key': api_key[1]
            }
        });
        logger.debug('deleted api key');
    } catch (error) {
        logger.error('could not delete api key', {
            error
        });
    }
};
const value = ([, apiKeyValue])=>apiKeyValue;
export var TokenType = /*#__PURE__*/ function(TokenType) {
    TokenType[TokenType["Okta"] = 1] = "Okta";
    TokenType[TokenType["Rocketlane"] = 2] = "Rocketlane";
    TokenType[TokenType["External"] = 3] = "External";
    TokenType[TokenType["Cognito"] = 4] = "Cognito";
    return TokenType;
}({});
const create = async (token, tokenType, clientId, externalRefKey, projectId)=>{
    var _response_data, _response_data_createdBy, _response_data1, _response_data2;
    logger.debug('fetching api key for access token');
    let params;
    const tokenPayload = getDecodedValue(token);
    if (tokenType === 2) {
        // LINK_MAGIC (RS256) must use X-Magic-Token — rocket-api can't verify it as ?token=.
        if (!isLinkMagicToken(token)) {
            params = {
                token
            };
        }
    } else if (tokenType === 3) {
        params = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'rl-token': token
        };
    }
    const response = await axios.post(tokenType === 3 ? '/users/invite-create-api-key' : '/users/create-api-key', 3 ? {
        externalRefKey: externalRefKey !== null && externalRefKey !== void 0 ? externalRefKey : undefined,
        projectId: projectId !== null && projectId !== void 0 ? projectId : undefined,
        user: {
            emailId: tokenPayload === null || tokenPayload === void 0 ? void 0 : tokenPayload.emailId
        }
    } : null, {
        baseURL: `${getCurrentApiHost()}/${API_NAMESPACE}/${API_VERSION}`,
        headers: tokenType === 1 || tokenType === 4 ? {
            Authorization: `Bearer ${token}`,
            'X-Client-ID': clientId
        } : tokenType === 2 && isLinkMagicToken(token) ? {
            'X-Magic-Token': token
        } : undefined,
        params
    });
    if (!((_response_data = response.data) === null || _response_data === void 0 ? void 0 : _response_data.apiKey)) {
        throw new Error('create-api-key returned no apiKey');
    }
    const api_key = [
        'api_key',
        response.data.apiKey,
        (_response_data1 = response.data) === null || _response_data1 === void 0 ? void 0 : (_response_data_createdBy = _response_data1.createdBy) === null || _response_data_createdBy === void 0 ? void 0 : _response_data_createdBy.userId,
        response.data.apiKeyId
    ];
    // add JWT Token expiry time to local storage
    logger.debug('Adding JWT Token expiry time to local storage');
    if ((_response_data2 = response.data) === null || _response_data2 === void 0 ? void 0 : _response_data2.newAuthExpiryTimestamp) {
        var _response_data3;
        localStorage.setItem(TOKEN_EXPIRY_KEY, (_response_data3 = response.data) === null || _response_data3 === void 0 ? void 0 : _response_data3.newAuthExpiryTimestamp);
    }
    logger.debug('fetched api key for access token');
    localStorage.setItem(key, JSON.stringify(api_key));
    InitWorkerApi.setData();
    logger.debug('set api key');
    // const marketplaceAuthTokenResponse = await axios.get('/users/cli-token', {
    //   baseURL: `${API_HOST}/${API_NAMESPACE}/${API_VERSION}`,
    // });
    // localStorage.setItem(
    //   MARKETPLACE_AUTH_TOKEN_QUERY_KEY,
    //   marketplaceAuthTokenResponse.data.token,
    // );
    logger.debug('clearing okta tokens');
    okta === null || okta === void 0 ? void 0 : okta.tokenManager.clear();
    logger.debug('cleared okta tokens');
    return api_key;
};
const tokens = ()=>_apiKey$.pipe(map((k)=>k == null ? null : k[1]), distinctUntilChanged(), shareReplay(1));
const header = ()=>{
    const result = get();
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return result == null ? {} : {
        'api-key': value(result)
    };
};
export const ApiKey = {
    get,
    exists,
    creator,
    create,
    clear,
    tokens,
    header
};
